import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { useStore } from '../../../../../stores/root';
import { SpinnerSize, Stack } from '@fluentui/react';
import { faFileInvoice, faMoneyCheckDollar, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import IncomeInvoiceService from '../../../../../services/finance/incomes/invoices/invoice';
import ErrorService from '../../../../../services/general/error';
import LoadingComponent from '../../../../feedbacks/loading';
import TotalIncomeInvoiceAmountCard from './cards/totalAmount';
import TotalIncomeInvoicePaidAmountCard from './cards/paidAmount';
import TotalIncomeInvoiceUnpaidAmountCard from './cards/unpaidAmount';
import TotalIncomeInvoicePendingAmountCard from './cards/pendingAmount';
import ButtonCards from '../../../../buttons/card';
import IncomeInvoicesList from '../../invoices/general/list';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import GeneralService from '../../../../../services/general';
import OrderService from '../../../../../services/orders/order';
import { IIncomeInvoiceResourceShortProps } from '../../../../../props/finance/incomes/invoices/invoice';
import { IOrderResourceShortProps } from '../../../../../props/orders/order';

type IncomeInvoicesSummaryProps = {
    startDate: string;
    endDate: string;
    basedOn?: string;
}

const IncomeInvoicesSummary: React.FC<IncomeInvoicesSummaryProps> = (props: IncomeInvoicesSummaryProps) => {
    const { banner, route } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [invoices, setInvoices] = React.useState<IIncomeInvoiceResourceShortProps[]>();
    const [orders, setOrders] = React.useState<IOrderResourceShortProps[]>();
    const [summary, setSummary] = React.useState<{
        invoicesOutstanding: number;
        invoicesOverdue: number;
        ordersOutstanding: number;
    }>({
        invoicesOutstanding: 0,
        invoicesOverdue: 0,
        ordersOutstanding: 0
    });

    useEffect(() => {
        _onRetrieveIncomesData();
    }, [props.startDate, props.endDate, props.basedOn]);

    const _onRetrieveIncomesData = async () => {
        try {
            setLoaded(false);
            setInvoices(undefined);

            const qs: string[] = [];
            if (props.basedOn === 'dueDate') {
                qs.push(`start_due_date=${props.startDate}`);
                qs.push(`end_due_date=${props.endDate}`);
            } else {
                qs.push(`start_date=${props.startDate}`);
                qs.push(`end_date=${props.endDate}`);
            }
            qs.push(`top=all`);

            const [_invoicesRes, _ordersRes] = await Promise.all([
                IncomeInvoiceService.retrieve(qs.join('&')),
                OrderService.retrieve('top=100&payment_status=unpaid&order_status=active;completed')
            ]);

            const invoiceList = _invoicesRes.data || [];
            const orderList = _ordersRes.data || [];

            let invOutstanding = 0;
            let invOverdue = 0;
            invoiceList.forEach((inv) => {
                if (Number(inv.unpaid || '0') > 0) {
                    invOutstanding += Number(inv.unpaid);
                }
            });

            let ordOutstanding = 0;
            orderList.forEach((ord) => {
                ordOutstanding += Number(ord.invoiceAmount || '0');
            });

            setSummary({
                invoicesOutstanding: invOutstanding,
                invoicesOverdue: invOverdue,
                ordersOutstanding: ordOutstanding
            });

            setInvoices(invoiceList);
            setOrders(orderList);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_income_invoices_summary_error',
                text: 'Failed to retrieve income invoices summary. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
            setLoaded(true);
        }
    }

    return <Stack tokens={{ childrenGap: 20 }} grow={1}>
        <Stack className={styles.cards} horizontal tokens={{ childrenGap: 20 }} styles={{ root: { width: '100%' } }}>
            <Stack styles={{ root: { width: '20%' } }}>
                <TotalIncomeInvoiceAmountCard invoices={invoices} />
            </Stack>
            <Stack styles={{ root: { width: '20%' } }}>
                <TotalIncomeInvoicePaidAmountCard invoices={invoices} />
            </Stack>
            <Stack styles={{ root: { width: '20%' } }}>
                <TotalIncomeInvoiceUnpaidAmountCard invoices={invoices} />
            </Stack>
            <Stack styles={{ root: { width: '20%' } }}>
                <TotalIncomeInvoicePendingAmountCard invoices={invoices} />
            </Stack>
            <Stack styles={{ root: { width: 'calc(20% - 20px)' } }}>
                <ButtonCards gap={15} buttons={[
                    { key: 'invoices', text: 'Invoices', variant: 'light', shadow: true, size: 'small', icon: faFileInvoice, onClick: () => route.route?.history.push("/finance/incomes/invoices") },
                    { key: 'payments', text: 'Payments', variant: 'light', shadow: true, size: 'small', icon: faMoneyCheckDollar, onClick: () => route.route?.history.push("/finance/incomes/payments") }
                ]} />
            </Stack>
        </Stack>
        <Stack className={'divider'}></Stack>
        <Stack horizontal tokens={{ childrenGap: 40 }}>
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: '50%' } }}>
                <Label size={'large'}>Invoices Summary</Label>
                <Stack horizontal styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 20 }}>
                    <Stack className='card' styles={{ root: { width: '50%' } }}>
                        <Text size={'medium'}>Outstanding Invoices</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-blue'} size={'xlarge'}>{GeneralService.getNumberWithSeparator((invoices || []).filter(i => Number(i.unpaid || '0') > 0).length)}</Label> : null}
                    </Stack>
                    <Stack className='card' styles={{ root: { width: '50%' } }}>
                        <Text size={'medium'}>Outstanding Amount</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-yellow'} size={'xlarge'}>Rp. {GeneralService.getNumberWithSeparator(summary.invoicesOutstanding)}</Label> : null}
                    </Stack>
                </Stack>
            </Stack>
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: '50%' } }}>
                <Label size={'large'}>Orders Summary</Label>
                <Stack horizontal styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 20 }}>
                    <Stack className='card' styles={{ root: { width: '50%' } }}>
                        <Text size={'medium'}>Outstanding Orders</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-yellow'} size={'xlarge'}>{GeneralService.getNumberWithSeparator((orders || []).length)}</Label> : null}
                    </Stack>
                    <Stack className='card' styles={{ root: { width: '50%' } }}>
                        <Text size={'medium'}>Outstanding Amount</Text>
                        {!loaded ? <LoadingComponent label={""} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
                        {loaded ? <Label className={'color-red'} size={'xlarge'}>Rp. {GeneralService.getNumberWithSeparator(summary.ordersOutstanding)}</Label> : null}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
        <Stack className={'divider'}></Stack>
        <Stack tokens={{ childrenGap: 10 }}>
            <Label size={'large'}>Income Invoices List</Label>
            <IncomeInvoicesList invoices={invoices} hideCreateButton={true} />
        </Stack>
    </Stack>;
};

export default IncomeInvoicesSummary;
